import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundaryProvider } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import Navbar from './components/Navbar';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import BreakevenReport from './components/BreakevenReport';
import ExpenseReport from './components/ExpenseReport';
import CashFlowReport from './components/CashFlowReport';
import RecurringExpenses from './components/RecurringExpenses';
import PayrollCalculator from './components/PayrollCalculator';
import FinancialTransactions from './components/FinancialTransactions';
import BusinessDebts from './components/BusinessDebts';
import UserManagement from './components/UserManagement';
import LocationProjection from './components/LocationProjection';
import DebtReduction from './components/DebtReduction';
import CompanySelector from './components/CompanySelector';
import { useAuth } from './hooks/useAuth';
import { useState } from 'react';

function App() {
  const { session, loading } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Protected route wrapper
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    if (!session) {
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  };

  return (
    <ErrorBoundaryProvider>
    <Router>
      <div className="min-h-screen bg-gray-100">
        <Toaster position="top-right" />
        <Routes>
          <Route path="/auth" element={
            session ? <Navigate to="/" replace /> : <Auth />
          } />
          
          <Route element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="container mx-auto pt-4 px-6">
                  <CompanySelector
                    selectedCompany={selectedCompany}
                    onCompanyChange={setSelectedCompany}
                  />
                </div>
                <div className="container mx-auto py-6">
                  <Routes>
                    <Route path="/" element={<Dashboard companyId={selectedCompany} />} />
                    <Route path="/breakeven" element={<BreakevenReport companyId={selectedCompany} />} />
                    <Route path="/expenses" element={<ExpenseReport companyId={selectedCompany} />} />
                    <Route path="/recurring" element={<RecurringExpenses companyId={selectedCompany} />} />
                    <Route path="/cashflow" element={<CashFlowReport companyId={selectedCompany} />} />
                    <Route path="/payroll" element={<PayrollCalculator companyId={selectedCompany} />} />
                    <Route path="/transactions" element={<FinancialTransactions companyId={selectedCompany} />} />
                    <Route path="/debts" element={<BusinessDebts companyId={selectedCompany} />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/projection" element={<LocationProjection companyId={selectedCompany} />} />
                    <Route path="/debt-reduction" element={<DebtReduction companyId={selectedCompany} />} />
                  </Routes>
                </div>
              </>
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard companyId={selectedCompany} />} />
            <Route path="/breakeven" element={<BreakevenReport companyId={selectedCompany} />} />
            <Route path="/expenses" element={<ExpenseReport companyId={selectedCompany} />} />
            <Route path="/recurring" element={<RecurringExpenses companyId={selectedCompany} />} />
            <Route path="/cashflow" element={<CashFlowReport companyId={selectedCompany} />} />
            <Route path="/payroll" element={<PayrollCalculator companyId={selectedCompany} />} />
            <Route path="/transactions" element={<FinancialTransactions companyId={selectedCompany} />} />
            <Route path="/debts" element={<BusinessDebts companyId={selectedCompany} />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/projection" element={<LocationProjection companyId={selectedCompany} />} />
            <Route path="/debt-reduction" element={<DebtReduction companyId={selectedCompany} />} />
          </Route>
        </Routes>
      </div>
    </Router>
    </ErrorBoundaryProvider>
  );
}

export default App;